import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface WarehouseFormData {
  code: string;
  name: string;
  type: 'MAIN' | 'TRANSIT' | 'CONSIGNMENT' | 'RETURNS';
  address?: string;
  city?: string;
  country?: string;
  isActive: boolean;
  isDefault: boolean;
}

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: string;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  isActive: boolean;
  isDefault: boolean;
}

interface WarehouseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouse?: Warehouse;
}

export function WarehouseModal({ open, onOpenChange, warehouse }: WarehouseModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!warehouse;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<WarehouseFormData>({
    defaultValues: {
      code: '',
      name: '',
      type: 'MAIN',
      address: '',
      city: '',
      country: '',
      isActive: true,
      isDefault: false,
    },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.warehouse.create.useMutation({
    onSuccess: () => {
      utils.warehouse.list.invalidate();
      showSuccessToast(t('common.created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.warehouse.update.useMutation({
    onSuccess: () => {
      utils.warehouse.list.invalidate();
      showSuccessToast(t('common.updated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      if (warehouse) {
        reset({
          code: warehouse.code,
          name: warehouse.name,
          type: warehouse.type as WarehouseFormData['type'],
          address: warehouse.address || '',
          city: warehouse.city || '',
          country: warehouse.country || '',
          isActive: warehouse.isActive,
          isDefault: warehouse.isDefault,
        });
      } else {
        reset({
          code: '',
          name: '',
          type: 'MAIN',
          address: '',
          city: '',
          country: '',
          isActive: true,
          isDefault: false,
        });
      }
    }
  }, [open, warehouse, reset]);

  const watchType = watch('type');

  const onSubmit = (data: WarehouseFormData) => {
    const payload = {
      ...data,
      address: data.address || undefined,
      city: data.city || undefined,
      country: data.country || undefined,
    };

    if (isEditing && warehouse) {
      updateMutation.mutate({
        id: warehouse.id,
        data: payload,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('warehouses.editWarehouse') : t('warehouses.addWarehouse')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('warehouses.editWarehouse')
              : t('warehouses.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('warehouses.code')}</Label>
              <Input
                id="code"
                {...register('code', { required: t('common.required') })}
                placeholder="WH01"
                disabled={isEditing}
              />
              {errors.code && (
                <p className="text-sm text-red-500">{errors.code.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">{t('warehouses.type')}</Label>
              <Select
                value={watchType}
                onValueChange={(value) => setValue('type', value as WarehouseFormData['type'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MAIN">{t('warehouses.main')}</SelectItem>
                  <SelectItem value="TRANSIT">{t('warehouses.transit')}</SelectItem>
                  <SelectItem value="CONSIGNMENT">{t('warehouses.consignment')}</SelectItem>
                  <SelectItem value="RETURNS">{t('warehouses.returns')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('warehouses.name')}</Label>
            <Input
              id="name"
              {...register('name', { required: t('common.required') })}
              placeholder="Main Warehouse"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t('warehouses.address')}</Label>
            <Input
              id="address"
              {...register('address')}
              placeholder="123 Industrial Road"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">{t('warehouses.city')}</Label>
              <Input
                id="city"
                {...register('city')}
                placeholder="Istanbul"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">{t('warehouses.country')}</Label>
              <Input
                id="country"
                {...register('country')}
                placeholder="Turkey"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                {...register('isActive')}
                className="h-4 w-4 border border-border"
              />
              <Label htmlFor="isActive">{t('warehouses.isActive')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                {...register('isDefault')}
                className="h-4 w-4 border border-border"
              />
              <Label htmlFor="isDefault">{t('warehouses.isDefault')}</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
